import { floridaMan, localHeadline } from './headlines';
import { needItem, needMoney, type GameContext, type Scene } from './types';
import type { IconToken } from '../icons/index';

export interface EventDef {
  id: string;
  weight: number;
  minDay?: number;
  /** Allowed hours [start, end). End may exceed 24 to run past midnight. */
  hours?: [number, number];
  cooldownDays?: number;
  once?: boolean;
  when?: (ctx: GameContext) => boolean;
  start: (ctx: GameContext) => Scene;
}

const news = (title: string, art: IconToken, text: string, leave = 'Aight'): Scene => ({
  title,
  art,
  text,
  choices: [],
  leave,
});

export const RANDOM_EVENTS: readonly EventDef[] = [
  {
    id: 'tornado',
    weight: 1.1,
    minDay: 3,
    hours: [12, 19],
    cooldownDays: 6,
    start: ({ world, sfx }) => {
      world.tornado();
      sfx('siren');
      return {
        title: ':warning: TORNADO WARNING :warning:',
        art: ':tornado:',
        text: "The sirens are going. Storm Chaser Chet just took his jacket off on Channel 6. Folks, that means it's serious. Everybody knows tornadoes are attracted to trailer parks. It's science.",
        leave: false,
        choices: [
          {
            label: 'Hide in the bathtub under a mattress',
            hint: 'Safe',
            run: ({ store, world }) => {
              store.incr('tornadoes');
              world.scatterScrap(10);
              return {
                text: "You ride it out in the tub with a mattress on top. When you climb out, the park is fine. Mostly. There's scrap metal everywhere that wasn't there before. The Lord provides.",
                minutes: 45,
                stats: { energy: -5 },
              };
            },
          },
          {
            label: 'Grab a beer and film it',
            hint: 'Risky · +Clout',
            run: ({ store, rng, world }) => {
              store.incr('tornadoes');
              world.scatterScrap(10);
              world.shake(0.6, 3);
              const hurt = rng.chance(0.45);
              const name = store.state.player.name;
              return {
                text: hurt
                  ? 'You film the whole thing. A flying lawn gnome clips you on the shoulder. Worth it: the video goes semi-viral.'
                  : 'You film the whole thing, beer in hand, narrating like a nature documentary. The video goes semi-viral. Chet retweets it.',
                clout: 15,
                money: rng.int(40, 120),
                stats: hurt ? { health: -30 } : {},
                headline: localHeadline('tornado', name),
                post: {
                  author: name,
                  avatar: 'player',
                  text: 'yall seein this?? :tornado::beer: (video)',
                  likes: rng.int(80, 300),
                },
              };
            },
          },
          {
            label: 'Lawn chair. Front row.',
            hint: '+Clout · Ouch',
            run: ({ store, world }) => {
              store.incr('tornadoes');
              world.scatterScrap(10);
              world.shake(0.4, 2);
              return {
                text: "You sit in a lawn chair and watch it roll past the water tower like it's the Fourth of July. Some debris hits you. You do not move. Legend.",
                clout: 8,
                stats: { health: -15 },
              };
            },
          },
        ],
      };
    },
  },
  {
    id: 'cops',
    weight: 1.2,
    minDay: 2,
    hours: [19, 26],
    cooldownDays: 4,
    start: ({ world }) => {
      world.cops();
      return {
        title: ':siren: BADGE BOYZ :siren:',
        art: ':police-car:',
        text: "Red and blue lights flood the park. Somewhere, reggae music starts playing for no reason. A deputy asks if you've seen a shirtless man with a rattail running between the lots.",
        leave: false,
        choices: [
          {
            label: '"I ain\'t seen nothin\'."',
            run: ({ store }) => {
              store.incr('noSnitch');
              store.setFlag('dale_favor', true);
              return {
                text: 'The deputy sighs and writes "nothin\'" on his notepad. Dale gives you a thumbs up from under Earl\'s truck.',
                clout: 6,
              };
            },
          },
          {
            label: 'Point the wrong way',
            run: ({ store }) => {
              store.incr('noSnitch');
              store.setFlag('dale_favor', true);
              return {
                text: 'You point toward the junkyard. The deputy runs that way. Dale blows you a kiss from the dumpster.',
                clout: 9,
              };
            },
          },
          {
            label: 'Snitch',
            hint: '+$40 · -Clout',
            run: ({ store }) => {
              store.setFlag('snitch', store.day);
              return {
                text: 'Crime Stoppers mails you $40. Nobody in the park will make eye contact with you for a week. Tammy is already typing.',
                money: 40,
                clout: -15,
                post: {
                  author: 'Tammy Lynn :lips: #BossBabe',
                  avatar: 'tammy',
                  text: 'Some people in this park are SNITCHES :rat: u know who u r',
                  likes: 37,
                },
              };
            },
          },
        ],
      };
    },
  },
  {
    id: 'possum',
    weight: 1,
    hours: [20, 27],
    cooldownDays: 3,
    when: ({ store }) => !store.hasDecor('possum'),
    start: () => ({
      title: 'Porch Possum',
      art: ':possum:',
      text: "There's a possum on your porch. It's screaming at you. You make eye contact. Neither of you blinks.",
      choices: [
        {
          label: 'Scream back',
          run: () => ({
            text: 'You scream. It screams. It plays dead. You both feel better about things.',
            clout: 2,
          }),
        },
        {
          label: 'Feed it a hot dog',
          hint: '-1 :hotdog:',
          requires: needItem('hotdog', 1, 'hot dog'),
          run: ({ store }) => {
            store.take('hotdog');
            return {
              text: 'It eats the hot dog in one bite and moves in. You name him Kevin. Kevin lives here now. Kevin pays no rent.',
              decor: 'possum',
              clout: 4,
            };
          },
        },
      ],
      leave: 'Leave it be',
    }),
  },
  {
    id: 'raccoons',
    weight: 1,
    hours: [18, 26],
    cooldownDays: 2,
    start: () => ({
      title: 'Trash Panda Uprising',
      art: ':raccoon:',
      text: 'The raccoons got into the dumpster again. There are at least six of them. One of them appears to be in charge.',
      choices: [
        {
          label: 'Negotiate with the leader',
          run: () => ({
            text: 'You and the head raccoon reach an understanding. He tosses you some cans as a gesture of good faith.',
            items: { cans: 4 },
            clout: 2,
          }),
        },
        {
          label: 'Join them',
          run: () => ({
            text: 'You dig through the dumpster alongside your new brothers. You find half a Hot Pocket. The raccoons respect you now.',
            stats: { hunger: 20, health: -5 },
            clout: 1,
          }),
        },
      ],
      leave: 'Close the blinds',
    }),
  },
  {
    id: 'repo',
    weight: 0.8,
    minDay: 3,
    hours: [10, 18],
    cooldownDays: 5,
    start: () => ({
      title: 'The Repo Man Cometh',
      art: ':lifted-truck:',
      text: "The repo man is here for Dale's truck. Dale is hiding in your shed. You don't have a shed. He's crouched behind your trailer, very visibly.",
      leave: false,
      choices: [
        {
          label: 'Stall him with small talk',
          hint: '30 min',
          run: ({ store }) => {
            store.setFlag('dale_favor', true);
            return {
              text: 'You talk about the weather for thirty straight minutes. The repo man gives up and goes to lunch. Dale says he owes you one. Dale owes everybody one.',
              minutes: 30,
              clout: 6,
            };
          },
        },
        {
          label: 'Point at Dale',
          hint: '+$25 · -Clout',
          run: () => ({
            text: "The repo man gives you a $25 'finder's fee.' Dale watches his truck leave on a flatbed. He'll get over it. He won't.",
            money: 25,
            clout: -10,
          }),
        },
      ],
    }),
  },
  {
    id: 'tammy_drama',
    weight: 1.2,
    hours: [9, 23],
    cooldownDays: 2,
    start: ({ store }) => {
      store.post({
        author: 'Tammy Lynn :lips: #BossBabe',
        avatar: 'tammy',
        text: 'Some people at Lot 7 need to learn how to return a casserole dish :eyeroll::eyeroll::eyeroll:',
        likes: 23,
      });
      return {
        title: ':phone: FaceSpace Drama',
        art: ':eyeroll:',
        text: 'Tammy Lynn just posted: "Some people at Lot 7 need to learn how to return a casserole dish :eyeroll::eyeroll::eyeroll:"\n\nThat\'s you. You\'re Lot 7.',
        choices: [
          {
            label: 'Comment "u ok hun?"',
            run: () => ({
              text: "Tammy replies 'I'm FINE :smile:'. She is not fine. The comment section is thriving.",
              clout: 3,
            }),
          },
          {
            label: 'Return the casserole dish',
            run: () => ({
              text: 'You return the dish. Tammy deletes the post. Then reposts it "for awareness."',
              clout: 2,
            }),
          },
          {
            label: 'Vaguebook her right back',
            run: ({ store, rng }) => {
              const backfire = rng.chance(0.35);
              return {
                text: backfire
                  ? 'Your vaguebook backfires. Darlene comments "TAKE IT TO DMS LADIES." Humiliating.'
                  : 'Your vaguebook absolutely cooks. 41 likes. Tammy goes "offline for my mental health" for an hour.',
                clout: backfire ? -4 : 7,
                post: {
                  author: store.state.player.name,
                  avatar: 'player',
                  text: 'Some ppl should worry about there OWN casserole dishes :nails:',
                  likes: backfire ? 3 : 41,
                },
              };
            },
          },
        ],
        leave: 'Ignore it',
      };
    },
  },
  {
    id: 'cookout',
    weight: 1,
    hours: [12, 19],
    cooldownDays: 3,
    start: () => ({
      title: "Earl's Cookout",
      art: ':ribs:',
      text: 'Big Earl is hosting a cookout. The smell of charcoal and lighter fluid fills the park. Somebody brought a guitar with five strings.',
      choices: [
        {
          label: 'Bring a six-pack',
          hint: '-6 :beer:',
          requires: needItem('beer', 6, 'beers'),
          run: ({ store }) => {
            store.take('beer', 6);
            return {
              text: "You show up with beer and become the hero of the cookout. Earl names a burger after you. It's mostly onion.",
              clout: 12,
              stats: { hunger: 60, buzz: 20 },
            };
          },
        },
        {
          label: 'Show up empty-handed',
          run: () => ({
            text: "Earl feeds you anyway. 'Next time bring somethin', freeloader.' He's smiling. Mostly.",
            stats: { hunger: 40 },
            clout: -2,
          }),
        },
      ],
      leave: 'Stay home',
    }),
  },
  {
    id: 'dale_scheme',
    weight: 0.8,
    hours: [10, 20],
    cooldownDays: 3,
    when: ({ store }) => !store.flag('dale_invest_day'),
    start: () => ({
      title: "Dale's Big Idea",
      speaker: { id: 'dale', name: 'Cousin Dale', voice: 210 },
      text: '\'Listen. Listen. Bottled swamp water. "Gator Juice." Electrolytes, baby. Thirty bucks gets you in on the ground floor.\'',
      choices: [
        {
          label: 'Invest $30',
          hint: '-$30',
          requires: needMoney(30),
          run: ({ store, rng }) => {
            store.spend(30);
            store.setFlag('dale_invest_day', store.day + 1);
            store.setFlag('dale_invest_win', rng.chance(0.45));
            return "Dale shakes your hand with both hands. That's a bad sign. 'See you tomorrow, partner.'";
          },
        },
      ],
      leave: 'Hard pass',
    }),
  },
  {
    id: 'power_outage',
    weight: 0.8,
    hours: [19, 23],
    cooldownDays: 5,
    start: () => ({
      title: 'Power Outage',
      art: ':candle:',
      text: "The power's out across the whole park. Everybody's outside. Earl's got the guitar. Tammy brought wine coolers. Honestly? It's kind of nice.",
      choices: [
        {
          label: 'Join the singalong',
          run: () => ({
            text: 'You sing every word to a song none of you know the name of. Jenkins cries a little. Best night in weeks.',
            clout: 5,
            stats: { energy: 10, buzz: 10 },
            minutes: 45,
          }),
        },
      ],
      leave: 'Go sit in the dark',
    }),
  },
  {
    id: 'lost_dog',
    weight: 1,
    hours: [9, 18],
    cooldownDays: 4,
    start: () => ({
      title: 'PEANUT IS LOOSE',
      speaker: { id: 'darlene', name: 'Darlene', voice: 330 },
      text: "'PEANUT GOT OUT! He's at large and he's ANGRY. Twenty-five bucks if you bring my baby home!'",
      choices: [
        {
          label: "I'm on it",
          run: ({ world }) => {
            world.lostDog();
            return 'Somewhere in the park, a four-pound chihuahua is barking at a lawnmower. Find Peanut. (Sprint to catch him!)';
          },
        },
      ],
      leave: 'Not my dog',
    }),
  },
  {
    id: 'church_ladies',
    weight: 0.8,
    hours: [10, 16],
    cooldownDays: 4,
    start: () => ({
      title: 'Church Ladies',
      art: ':casserole:',
      text: 'Two church ladies are going door to door with casseroles and pamphlets. They have seen you. They are coming.',
      choices: [
        {
          label: 'Accept graciously',
          run: () => ({
            text: 'You get a casserole and a 20-minute talk about your soul. Fair trade. The casserole slaps.',
            stats: { hunger: 50 },
            minutes: 20,
            clout: 1,
          }),
        },
        {
          label: "Pretend you're not home",
          run: () => ({
            text: 'You hide behind the couch. They leave a casserole on your steps anyway. Bless them.',
            stats: { hunger: 30 },
          }),
        },
      ],
      leave: false,
    }),
  },
  {
    id: 'yard_sale',
    weight: 0.8,
    hours: [9, 15],
    cooldownDays: 4,
    start: () => ({
      title: "Tammy's Yard Sale",
      art: ':price-tag:',
      text: 'Tammy is having a yard sale. It is mostly porcelain angels and a treadmill being used as a clothes rack.',
      choices: [
        {
          label: 'Buy the mystery box',
          hint: '-$5',
          requires: needMoney(5),
          run: ({ store, rng }) => {
            store.spend(5);
            const roll = rng.int(0, 4);
            if (roll === 0)
              return {
                text: 'Inside: two scratch-offs, unscratched! Tammy has never scratched a ticket in her life.',
                items: { scratcher: 2 },
              };
            if (roll === 1)
              return {
                text: 'Inside: a Freedom Pack of fireworks and a note that says "for emergencies."',
                items: { fireworks: 1 },
              };
            if (roll === 2)
              return { text: 'Inside: six warm beers. Warm beer is still beer.', items: { beer: 6 } };
            if (roll === 3 && !store.hasDecor('gnomes'))
              return {
                text: 'Inside: three garden gnomes! They are armed (with tiny shovels). They guard your lawn now.',
                decor: 'gnomes',
              };
            return {
              text: 'Inside: a single Croc. Not your size. Not even a left one. You keep it out of respect.',
            };
          },
        },
      ],
      leave: 'Just browsing',
    }),
  },
  {
    id: 'heat_wave',
    weight: 0.7,
    hours: [9, 12],
    cooldownDays: 5,
    start: ({ store }) => {
      store.setFlag('heatwave', store.day);
      return news(
        'HEAT ADVISORY',
        ':overheated:',
        "It's 104°F. The asphalt is soft. Earl is sitting in a kiddie pool in his jorts. You'll get tired faster today. Pools and tubs hit extra hard.",
        'Sweat it out',
      );
    },
  },
  {
    id: 'florida_cousin',
    weight: 0.9,
    cooldownDays: 3,
    start: ({ store, rng }) => {
      const headline = floridaMan(rng);
      store.headline(`BREAKING: ${headline}`);
      store.addClout(2);
      return news(
        'Phone Call From Florida',
        ':telephone:',
        `Your cousin from Florida called. He's on the news again:\n\n"${headline}"\n\nHe says it's not what it looks like. It is exactly what it looks like.`,
        'Proud of him',
      );
    },
  },
  {
    id: 'jackpot_day',
    weight: 0.6,
    hours: [8, 12],
    cooldownDays: 6,
    start: ({ store }) => {
      store.setFlag('jackpot_day', store.day);
      return news(
        'DOUBLE PRIZE DAY',
        ':slots:',
        'Gas-N-Grub is running DOUBLE PRIZE DAY on Lucky 7s scratch-offs! Every winning ticket pays double, today only. Your retirement plan just got a boost.',
        "Feelin' lucky",
      );
    },
  },
  // --- the Back Forty ----------------------------------------------------------------
  {
    id: 'raccoon_mugging',
    weight: 1.4,
    hours: [19, 28],
    cooldownDays: 2,
    when: ({ world }) => world.zone() === 'backForty',
    start: () => ({
      title: 'Back Forty Mugging',
      art: ':raccoon:',
      text: 'A raccoon the size of a toddler steps out from behind a burn barrel and starts going through your pockets. It does not seem worried about you at all.',
      choices: [
        {
          label: 'Let him have a hot dog',
          hint: '-1 :hotdog:',
          requires: needItem('hotdog', 1, 'hot dog'),
          run: ({ store }) => {
            store.take('hotdog');
            return {
              text: 'He takes the hot dog, makes eye contact, and leaves. You have an understanding now.',
              clout: 2,
            };
          },
        },
        {
          label: 'Wrestle him for it',
          hint: 'Risky',
          run: ({ rng }) =>
            rng.chance(0.5)
              ? {
                  text: 'You win! Somehow! Trenchcoat Terry saw the whole thing and tells everyone. You are a legend at the barrels.',
                  clout: 5,
                  stats: { health: -6 },
                }
              : {
                  text: 'You lose. Badly. He takes $5 and your dignity, and he does not need the dignity.',
                  money: -5,
                  stats: { health: -14 },
                },
        },
      ],
      leave: 'Hiss and back away slowly',
    }),
  },
  {
    id: 'ghost_light',
    weight: 1.2,
    hours: [21, 28],
    cooldownDays: 3,
    when: ({ world }) => world.zone() === 'backForty',
    start: () => ({
      title: 'A Light at Lot 13',
      art: ':candle:',
      text: "A light is flickering inside the burned-out shell at Lot 13. Nobody lives there. Nobody's lived there since 1998.",
      choices: [
        {
          label: 'Go look',
          run: ({ store, rng }) => {
            store.unlock('lot13');
            store.incr('lot13');
            return rng.chance(0.5)
              ? {
                  text: "It's a candle in a coffee can, next to a note: 'TAKE ONE, LEAVE ONE.' There's a twenty in it. You take it. You leave a can of Nattie Lite. Fair's fair.",
                  money: 20,
                }
              : {
                  text: 'The light goes out as you get close. The rocking chair is rocking. You leave so fast your Crocs switch to sport mode on their own.',
                  stats: { energy: -10 },
                  clout: 3,
                };
          },
        },
      ],
      leave: 'Absolutely not',
    }),
  },
  {
    id: 'revenuers',
    weight: 1.1,
    hours: [10, 18],
    cooldownDays: 5,
    minDay: 3,
    when: ({ store }) => store.has('shine'),
    start: () => ({
      title: 'The Revenuers',
      art: ':revenuer:',
      text: "Two men in short-sleeve dress shirts and clip-on ties walk up. 'Afternoon. We're with the county. We've had reports of an unlicensed distillation operation.' One of them is sniffing the air.",
      choices: [
        {
          label: 'Play dumb',
          run: ({ store, rng }) => {
            if (rng.chance(0.6))
              return {
                text: "'Distill-what now?' They look at each other, look at your Crocs, and decide you couldn't possibly. They leave.",
                clout: 2,
              };
            const n = store.count('shine');
            store.give('shine', -n);
            return {
              text: `They check your trailer and find ${n} mason jar${n > 1 ? 's' : ''} of "pickle juice." Confiscated. You swear one of them winked.`,
            };
          },
        },
        {
          label: "Offer 'em a taste",
          hint: '-1 :moonshine:',
          run: ({ store }) => {
            store.take('shine');
            return {
              text: "The quiet one takes a sip and his eyes water. 'For, uh, evidence.' They take the jar, leave a twenty for 'the inconvenience' and never come back.",
              money: 20,
            };
          },
        },
        {
          label: 'Run',
          run: () => ({
            text: "You run. They don't chase you, because they're county employees and it's 2:30. You keep your shine and lose your breath.",
            stats: { energy: -15 },
            clout: 2,
          }),
        },
      ],
      leave: false,
    }),
  },
];

// --- scheduled (non-random) events -----------------------------------------------

export function apexTakeoverScene(): Scene {
  return {
    title: 'A Letter From Your New Landlord',
    art: ':briefcase:',
    text: 'Dear Valued Resident,\n\nWe are thrilled to announce that Lucky Acres is now an APEX CAPITAL HOLDINGS LLC community! We look forward to "unlocking value" together.\n\nEffective immediately, lot rent will increase $25 every week to reflect "market conditions."\n\nWarmly,\nA Guy Named Brent',
    choices: [
      {
        label: 'Read the fine print',
        run: () =>
          "The fine print says 'amenities' now refers to the dumpster. There is also a $9.99 'convenience fee' for the privilege of paying rent. Darlene crossed that part out with a Sharpie. Bless her.",
      },
    ],
    leave: 'Crumple it up',
  };
}

export function taxRefundScene(): Scene {
  return news(
    'TAX REFUND SEASON',
    ':money-wings:',
    "Your tax refund just hit! +$600. Somewhere, a 75-inch TV at Lucky's Pawn just felt a disturbance in the Force.",
    'Treat yo self (responsibly)',
  );
}
